





































































































import Vue from 'vue'

import { mapState } from 'vuex'
import { formatDate, hhmmss } from '@/helpers/general'
import MapContainer from '@/components/Reports/MapContainer.vue'
import Toolbar from '@/components/Reports/Toolbar.vue'
import Download from '@/components/Reports/Download.vue'
import RequestOverview from '@/components/Reports/RequestOverview.vue'
import moment from 'moment'
// FP-TS imports
import { flow } from 'fp-ts/lib/function'
import { map as fmap, reduce, sort, reverse } from 'fp-ts/Array'
import { Ord, contramap, ordDate } from 'fp-ts/lib/Ord'

// Types
import { Trip } from '@/components/Trips/types'
import { Report } from '@/components/Reports/types'
import { User } from '@/components/Users/constants'
import { apiDropdownsEntities, apiDropdownsEntityReports, apiGetEntityReports } from '@/api'

const byStartDateTime: Ord<Trip> = contramap((t: Trip) => new Date(t.trip_started_at))(ordDate)

export default Vue.extend({
  components: {
    MapContainer,
    Toolbar,
    Download,
    RequestOverview
  },
  data: () => ({
    snackbar: true,
    expanded: [],
    dateFormat: 'DD-MM-YYYY',
    selectedId: '',
    loadingText: 'Loading reports...',
    notice: false,
    searched: false,
    reports: [],
    loadingReports: false,
    entities: [],
    loadingEntities: true,
    users: [],
    loadingUsers: true,
    footerProps: {
      showFirstLastPage: false,
      itemsPerPageOptions: [10, 25, 50, 100, -1]
    },
    range: {
      from: '',
      till: ''
    },
    selectedEntity: null,
    selectedUser: null,
    entity: null,
    rowsPerPageItems: [15, 20, 25, 50, 100],
    pagination: {
      page: 1,
      rowsPerPage: 25
    }
  }),
  computed: {
    ...mapState(['user']),
    ...mapState('Trips', ['trips']),
    reportsTotals () {
      type Report = {
        distance: number;
        average_speed: number;
      }

      if (this.reports.length === 0) {
        return { distance: 0, averageSpeed: 0 }
      }

      let distance = this.reports.map((r: Report) => r.distance).reduce((prev: number, curr: number) => prev + curr)
      let averageSpeed = this.reports.map((r: Report) => r.average_speed).reduce((prev: number, curr: number) => prev + curr)
      distance = Number(distance).toFixed(2)
      averageSpeed = (averageSpeed / this.reports.length).toFixed(2)

      return { distance, averageSpeed }
    },
    reportsWithWaitTimes (): Trip[] {
      const reportsAccumulator = {
        lastTrip: {
          trip_started_at: null
        },
        recalculatedTrips: []
      }
      const sortedTripsWithIdleTime: any | Trip[] = flow( // TODO: hacky...
        sort(byStartDateTime),
        reverse,
        fmap(this.subtractTwoMinutesToEndTime),
        reduce(reportsAccumulator, this.updateTripsWithIdleTime),
        (ts: Record<string, any | Trip>): Trip[] => ts.recalculatedTrips, // Use lenses
        fmap(this.mkIdleTimeStrOrDefault)
      )(this.reports)
      return sortedTripsWithIdleTime
    },
    headers () {
      return [
        { value: 'trip_started_at', text: 'startTime' },
        { value: 'start_address', text: 'startAddress' },
        { value: 'trip_ended_at', text: 'stopTime' },
        { value: 'end_address', text: 'stopAddress' },
        { value: 'idle', text: 'idle' },
        { value: 'distance', text: 'distance' },
        { value: 'duration', text: 'duration' },
        { value: 'average_speed', text: 'averageSpeed' }
      ].map(this.mkHeader)
    },
    noDataText (): string {
      if (this.loadingReports) return this.loadingText

      const dateSelected = this.range.from && this.range.till
      const conditions = [
        { cond: !this.selectedUser && !this.selectedEntity && !dateSelected, value: 'all' },
        { cond: !dateSelected && !dateSelected, value: 'date' },
        { cond: this.searched, value: 'searched' }
      ]
      const text = conditions.reduce(this.resolveCondition, { found: false, value: 'clickSearch' })
      return this.t({ s: `table.noDataText.${text.value}`, component: 'Reports' })
    }
  },
  mounted (): void {
    apiDropdownsEntityReports()
      .then((response: any) => {
        this.entities = response?.data?.data

        this.loadingEntities = false
      })
  },
  methods: {
    calculateIdleTimeBetweenTrips (first: string, last: string): string {
      const ms = moment(last).diff(moment(first))
      const d = moment.duration(ms)
      return Math.floor(d.asHours()) + moment.utc(ms).format(':mm')
    },
    subtractTwoMinutesToEndTime: (t: Trip): Trip => ({ ...t, trip_ended_at: moment(t.trip_ended_at).subtract(2, 'minutes').format('YYYY-MM-DD HH:mm') }),
    mkIdleTimeStrOrDefault: (t: Trip): Trip => ({ ...t, idle: t.idle === 'NaNInvalid date' ? '' : `${t.idle}h` }),
    updateTripsWithIdleTime (acc: any, r: Trip): Record<string, Record<string, any> | Trip[]> {
      const idle = this.calculateIdleTimeBetweenTrips(r.trip_ended_at, acc.lastTrip.trip_started_at)

      return {
        lastTrip: r,
        recalculatedTrips: [...acc.recalculatedTrips, { ...r, idle }]
      }
    },
    showNotice (show: boolean) {
      this.notice = show
    },
    enableZendesk (): void {
      window?.zE?.activate?.()
    },
    resolveCondition: (result: Record<string, boolean | string>, data: any) => !result.found && data.cond
      ? { found: true, value: data.value }
      : result,
    mkHeader ({ value, text }: Record<string, string>) {
      return {
        value,
        text: this.t({ s: `table.headers.${text}`, component: 'Reports' }),
        sortable: value !== 'data-table-expand'
      }
    },
    mkUser (u: User): User {
      return {
        ...u,
        name: `${u.first_name} ${u.last_name}`
      }
    },
    getTrips (query: any) {
      const { selectedEntity, selectedUser, range } = query
      if (!selectedEntity && !selectedUser) {
        return
      }

      if (!range || !range.from || !range.till) {
        return
      }

      this.range = range
      this.selectedEntity = selectedEntity
      this.selectedUser = selectedUser

      this.loadingReports = true
      this.searched = false
      this.reports = []

      apiGetEntityReports(range.from, range.till, selectedEntity.id)
        .then(({ data }: any) => {
          this.reports = data.data.map(this.mkReport)
          this.loadingReports = false
        })
        .catch((e: string) => { this.reports = [] })
        .finally(this.resetSearch)
    },
    mkReport (trip: Trip): Report {
      const format = 'YYYY-MM-DD HH:mm'

      return {
        id: trip.id,
        trip_started_at: formatDate(trip.trip_started_at, format),
        start_address: trip.start_address?.replace(/,/g, ' ') || '',
        trip_ended_at: formatDate(trip.trip_ended_at, format),
        end_address: trip.end_address?.replace(/,/g, ' ',) || '',
        duration: hhmmss(trip.duration) || 0,
        distance: Math.round((trip.distance / 1000) * 100) / 100 || 0.00,
        average_speed: trip.average_speed
          ? Math.round((trip.average_speed) * 100) / 100
          : 0
      }
    },
    resetSearch () {
      this.loadingReports = false
      this.searched = true
    },
    handleRowClick (row: any) {
      const expanded = this.expanded
      const hasMatchingID = (r: any) => r.id === row.id
      this.expanded = expanded.some(hasMatchingID)
        ? expanded.filter((r: any) => !hasMatchingID(r))
        : [...expanded, row]
    }
  }
})
